import { isPresent } from "./checks";
import { CruiseTemplateSuiteFragment } from "types/graphql-types";
import { UnitSystem } from "./localization/geolocation/geolocation";
import { isString } from "./strings";
import { SuitePriceV2 } from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { isPriceWaitlist } from "components/Prices/utils";
import { LinguiT } from "./localization/lingui-helpers";
import { msg } from "@lingui/core/macro";
import { Price } from "@silversea-ssc/ui-components-react";

export function priceForSuite<T extends SuitePriceV2>(
  prices: readonly T[] | undefined,
  suiteCodes?: (string | undefined)[]
): T | undefined {
  if (!prices || !suiteCodes) return undefined;

  const suitePrices = suiteCodes
    .map((suiteCode) => prices.find((p) => p.suiteCategory === suiteCode))
    .filter(isPresent);

  return suitePrices.find((x) => !isSuiteWaitlist(x)) || suitePrices[0];
}

const isSuiteWaitlist = (suitePrice: SuitePriceV2): boolean =>
  suitePrice.prices.every((p) => isPriceWaitlist(p.price));

export const isSuitePriceWaitlist = (prices: Price[]): boolean =>
  prices.every((p) => isPriceWaitlist(p));

const formatDecks = (
  t: LinguiT,
  decks: (number | undefined)[] | undefined,
  totalDecks: number | undefined
): string | undefined => {
  const deckList = decks?.filter(isPresent)?.join(", ");
  if (totalDecks)
    return `${t(msg`deck`)} ${deckList} ${t(
      msg`count-separator-xx-of-xx`
    )} ${totalDecks}`;

  return deckList;
};

const formatSuiteSize = (
  t: LinguiT,
  suite: CruiseTemplateSuiteFragment,
  suiteCategory: string,
  unit?: UnitSystem
): string | undefined => {
  const configuration = configurationBySuiteCategory(suite, suiteCategory);
  const size =
    unit == "feet"
      ? configuration?.shortSizeDescriptionInSquareFeet?.localized
      : configuration?.shortSizeDescriptionInSquareMetres?.localized;

  if (size) return `${t(msg`Suite size`)} ${size}`;

  return undefined;
};

const formatVerandaSize = (
  t: LinguiT,
  suite: CruiseTemplateSuiteFragment,
  suiteCategory: string,
  unit?: UnitSystem
): string | undefined => {
  const configuration = configurationBySuiteCategory(suite, suiteCategory);
  const size =
    unit == "feet"
      ? configuration?.verandaSizeInSquareFeet?.localized
      : configuration?.verandaSizeInSquareMetres?.localized;

  if (size) return `${t(msg`Veranda size`)} ${size}`;

  return undefined;
};

const configurationBySuiteCategory = <
  T extends { configurations?: ({ code?: string } | undefined)[] }
>(
  suite: T,
  suiteCategory: string
): NonNullable<T["configurations"]>[0] =>
  suite?.configurations?.find((x) => x?.code == suiteCategory) ||
  suite?.configurations?.[0];

export const suiteCardFeatures = (
  t: LinguiT,
  content: CruiseTemplateSuiteFragment,
  suiteCategory: string,
  totalDecks: number | undefined,
  unit: UnitSystem | undefined
): string[] => {
  const verandaSize = formatVerandaSize(t, content, suiteCategory, unit);
  const verandaSizeOrVeranda = verandaSize
    ? verandaSize
    : content.keyFeatures?.veranda
      ? t(msg({ id: "keyFeature_veranda", message: "Veranda" }))
      : undefined;

  return [
    formatDecks(t, content.deck, totalDecks),
    formatSuiteSize(t, content, suiteCategory, unit),
    verandaSizeOrVeranda,
    content.keyFeatures?.livingRoom
      ? t(msg({ id: "keyFeature_livingRoom", message: "Living room" }))
      : undefined
  ].filter(isString);
};
